import { Dispatch, ReactNode, RefObject, createContext, useContext } from 'react';

export const HidingStrategy = {
  REMOVE_FROM_DOM: 'remove_from_dom',
  CSS_ONLY: 'css_only',
} as const;
export type HidingStrategy = (typeof HidingStrategy)[keyof typeof HidingStrategy];

export type Tab = {
  id: string;
  orderKey?: number;
  disabled: boolean;
  buttonLabel: ReactNode;
  tooltip?: string;
  ref?: RefObject<HTMLButtonElement | null>;
};

export type FetchingData = {
  fetchFormParams: any;
  isLoading: boolean;
  fetchError?: boolean;
  requestError?: string | null;
  loadingMessage: string;
  tabIndex?: number;
};
type TabContext = {
  tabs: Tab[];
  isActiveItem(id: string): boolean;
  registerOrUpdateTabItem(tab: Tab): void;
  unregisterTabItem(tabId: string): void;
  setActiveTab(tabId: string): void;
  hasPreviousTab(id: string | undefined): boolean;
  hasNextTab(id: string | undefined): boolean;
  focusById(tabId: string): void;
  getCurrentTabIndexById(id: string): number;
  getNextTabIndex(id: string | undefined): number;
  getNextTab(id: string | undefined): Tab;
  getPreviousTab(id: string | undefined): Tab;
  getPreviousTabIndex(id: string | undefined): number;
  isNextTabDisabled(id: string | undefined): boolean;
  isPreviousTabDisabled(id: string | undefined): boolean;
  hidingStrategy: HidingStrategy;
  footerButtonsClicked: boolean;
  setFooterButtonsClicked: Dispatch<boolean>;
  fetchingData: FetchingData | null;
};
const TabBarContext = createContext<TabContext | null>(null);

TabBarContext.displayName = 'TabBarContext';

export const useTabBarContext = () => {
  const context = useContext(TabBarContext);
  if (context === null) {
    throw new Error('useTabBarContext is used without Provider');
  }
  return context;
};
export default TabBarContext;
